import { createContext, useContext, type ReactNode } from 'react';
import { useBoardView } from '@/features/board/useBoardView';
import { useTasksRealtime, type RealtimeStatus } from './useTasksRealtime';

const Context = createContext<RealtimeStatus>('connecting');

/**
 * ONE subscription, held at the shell.
 *
 * It used to live in BoardScreen, which was wrong twice over. Two components wanting
 * to show the indicator would have opened two channels — the hook subscribes, it does
 * not just report — and the socket died whenever you left the board, so the
 * notification badge stopped updating on the People screen.
 */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  /*
    The SAME scope the board queries under, not a constant.

    It is the query key these events invalidate. An admin narrowing the board to their
    own cards changes that key, and a handler still invalidating the old one would
    leave the board looking live while nothing on it ever moved.
  */
  const { scope } = useBoardView();
  const status = useTasksRealtime(scope);
  return <Context.Provider value={status}>{children}</Context.Provider>;
}

export const useRealtimeStatus = () => useContext(Context);

const LABEL: Record<RealtimeStatus, { text: string; color: string }> = {
  live: { text: 'Live', color: 'var(--color-status-completed)' },
  connecting: { text: 'Connecting', color: 'var(--color-status-pending)' },
  reconnecting: { text: 'Reconnecting', color: 'var(--color-status-pending)' },
  offline: { text: 'Offline', color: 'var(--color-status-cancelled)' },
};

/**
 * Dot plus label, on every screen.
 *
 * The label is not decoration: a coloured dot alone only reads to someone who already
 * knows what it stands for, and nobody using this has been told. Two saved characters
 * in a header are not worth a control nobody can interpret.
 */
export function LiveIndicator() {
  const status = useRealtimeStatus();
  const { text, color } = LABEL[status];

  return (
    <span
      className="flex shrink-0 items-center gap-1.5 text-[11px]"
      style={{ color: 'var(--text-muted)' }}
      title={status === 'live' ? 'Live — updates arrive without refreshing' : text}
      aria-label={`Connection: ${text}`}
      role="status"
    >
      <span aria-hidden className="size-1.5 rounded-full" style={{ background: color }} />
      {text}
    </span>
  );
}
